import { useEffect, useRef, useState } from "react";

/** Duração padrão da contagem, em ms. */
export const COUNT_UP_DURATION = 900;

/**
 * Easing de saída (cúbica): rápido no começo, desacelerando no fim. É o que dá
 * a sensação de "assentar" no valor em vez de parar seco.
 */
export function easeOutCubic(t: number): number {
  const clamped = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - clamped, 3);
}

/** Valor intermediário da contagem num instante `elapsed` da duração. */
export function countUpValueAt(
  target: number,
  elapsed: number,
  duration: number = COUNT_UP_DURATION,
): number {
  if (!Number.isFinite(target)) return 0;
  if (duration <= 0 || elapsed >= duration) return target;
  if (elapsed <= 0) return 0;
  return target * easeOutCubic(elapsed / duration);
}

/** `true` quando o usuário pediu menos animação no sistema operacional. */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Anima de 0 até `target` sempre que o alvo muda.
 *
 * Devolve o valor final de imediato quando o usuário pede menos animação —
 * requisito de acessibilidade, e também evita contagem em teste/SSR, onde
 * `requestAnimationFrame` pode não existir.
 */
export function useCountUp(
  target: number,
  duration: number = COUNT_UP_DURATION,
): number {
  const [value, setValue] = useState(target);
  const frameRef = useRef<number>();

  useEffect(() => {
    if (
      prefersReducedMotion() ||
      typeof requestAnimationFrame === "undefined" ||
      !Number.isFinite(target)
    ) {
      setValue(Number.isFinite(target) ? target : 0);
      return;
    }

    const startedAt = performance.now();
    const step = (now: number) => {
      const elapsed = now - startedAt;
      setValue(countUpValueAt(target, elapsed, duration));
      if (elapsed < duration) {
        frameRef.current = requestAnimationFrame(step);
      }
    };

    setValue(0);
    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration]);

  return value;
}
